import { gql, useMutation } from "@apollo/client";
import { useRouter } from "next/router";

import { BiChevronRight } from "react-icons/bi";
import { PickupAnswer, pickupLocationType } from "../..";
import { Iso639 } from "../../../../../graphql/operations";
import { formatDate } from "../../../../../utils/calendarUtils";
import Button from "../../../../Atoms/Button";
import Loader from "../../../../Atoms/Loader";
import { FormStateInput, validateFormInput } from "./validation";
type Props = {
  productId: string;
  currencyCode: string;
  languageCode: Iso639;
  formInput: FormStateInput;
  disabled?: boolean;
  isOrderRequest?: boolean;
  pickupAnswer?: PickupAnswer;
  pickupLocation?: pickupLocationType;
};

const makeOrderUrl = (orderId: string, isOrderRequest: boolean) => {
  if (isOrderRequest) {
    return `/bookings/${orderId}?type=request`;
  }

  return `/bookings/${orderId}`;
};

export default function CheckoutSubmitButton({
  productId,
  disabled = false,
  isOrderRequest = false,
  currencyCode,
  languageCode,
  formInput,
  pickupAnswer,
  pickupLocation,
}: Props) {
  const router = useRouter();
  const [createBooking, { loading, error, data }] = useMutation(CREATE_BOOKING);

  const handleClick = () => {
    const [errors, result] = validateFormInput({
      ...formInput,
      ...{ pickupAnswer, pickupLocation },
    });

    if (errors || !result) {
      return;
    }

    if (window) {
      window.localStorage.setItem("fname", result.names[0].givenName);
      window.localStorage.setItem("lname", result.names[0].familyName);
      window.localStorage.setItem("email", result.email);
      window.localStorage.setItem("confirmEmail", result.confirmEmail);
      window.localStorage.setItem("contactNumber", result.contactNumber);
    }

    const {
      names,
      specialRequests,
      reservationTime,
      reservationDate,
      acceptsMarketing,
      contactNumber,
      email,
      couponCode,
      details,
      guideId,
      isPrivate,
    } = result;

    const bookDate = formatDate(reservationDate);
    const bookTime = reservationTime;

    createBooking({
      variables: {
        input: {
          priceTypes: details.map((d) => ({
            priceTypeId: d.typeId,
            quantity: d.quantity,
          })),
          productId,
          date: bookDate,
          time: bookTime,
          currency: currencyCode,
          guests: names.map((name, index) => ({ ...name, index })),
          email,
          phoneNumber: contactNumber,
          optInMailingList: acceptsMarketing,
          couponCode,
          specialRequests,
          isPrivate,
          guideId,
          pickupLocation,
        },
      },
    });
  };

  if (loading) {
    return (
      <Button large disabled>
        <Loader size={20} color="#fff" thickness={2} />
      </Button>
    );
  }

  if (error) {
    return <>{error.graphQLErrors.join(", ")}</>;
  }

  if (data && data.createBooking) {
    const redirectTo = makeOrderUrl(data.createBooking.id, isOrderRequest);
    router.push(redirectTo);
  }

  return (
    <div className="ProceedBtnContainer">
      <Button disabled={disabled} large onClick={handleClick}>
        {isOrderRequest ? <>Submit request</> : <>Proceed to payment</>}
      </Button>
      <BiChevronRight
        color={disabled ? "#aaa" : "#fff"}
        size={50}
        style={{
          position: "absolute",
          right: 3,
          top: 3,
        }}
      />
    </div>
  );
}

const CREATE_BOOKING = gql`
  mutation CreateBookingMutation($input: CreateBookingInput!) {
    createBooking(input: $input) {
      id
      state
    }
  }
`;
